import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Landmark, FileDown, FileText, AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Mail } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const eur = (n) => Number(n || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function AdminPstgReportPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [year, setYear] = useState(String(new Date().getFullYear() - 1));
    const [rep, setRep] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notifying, setNotifying] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const { data } = await api.get(`/admin/psttg/report?year=${year}`);
            setRep(data);
        } catch {
            toast.error("Report konnte nicht geladen werden");
        } finally {
            setBusy(false);
        }
    }, [year]);

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") { navigate("/"); return; }
        load();
    }, [user, loading, navigate, load]);

    const dl = async (kind) => {
        try {
            const res = await api.get(`/admin/psttg/export.${kind}?year=${year}`, { responseType: "blob" });
            const u = window.URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = u; a.download = `psttg_dac7_${year}.${kind}`;
            document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(u);
        } catch { toast.error("Export fehlgeschlagen"); }
    };

    const notifyIncomplete = async () => {
        setNotifying(true);
        try {
            const { data } = await api.post(`/admin/psttg/notify-incomplete?year=${year}`);
            if (data.notified_count === 0) {
                toast.info("Keine unvollständigen meldepflichtigen Händler (mit E-Mail) gefunden.");
            } else {
                toast.success(`${data.notified_count} Händler angeschrieben${data.mocked ? " (Versand simuliert — kein Mail-Key)" : ""}.`);
            }
        } catch { toast.error("Versand fehlgeschlagen"); }
        finally { setNotifying(false); }
    };

    const s = rep?.summary;
    return (
        <div className="min-h-screen bg-gray-50" data-testid="admin-psttg-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <button onClick={() => navigate("/admin")} className="text-xs text-gray-400 hover:text-white" data-testid="admin-psttg-back">← Admin-Dashboard</button>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-2 mt-4">Plattform · Steuertransparenz</div>
                    <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight flex items-center gap-3" data-testid="admin-psttg-title">
                        <Landmark className="w-9 h-9 text-[#FFCC00]" /> PStTG / DAC7-Meldung
                    </h1>
                    <p className="text-sm text-gray-400 mt-2 max-w-3xl">
                        Meldevorbereitung für Plattformbetreiber: meldepflichtige Anbieter je Meldejahr (Bagatellgrenze: &lt; 30 Verkäufe UND &lt; 2.000 €).
                        Übersicht + Export — die finale Meldung erfolgt über das BZSt (DIP) bis 31.01. des Folgejahres.
                    </p>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
                {/* Jahres-Wahl + Export */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setYear(String(Number(year) - 1))} className="h-9 w-9 flex items-center justify-center rounded-sm border border-gray-300 bg-white hover:bg-gray-50" data-testid="admin-psttg-prev" aria-label="Vorheriges Jahr"><ChevronLeft className="w-4 h-4" /></button>
                        <span className="font-display text-2xl font-black px-2" data-testid="admin-psttg-year">{year}</span>
                        <button onClick={() => setYear(String(Number(year) + 1))} className="h-9 w-9 flex items-center justify-center rounded-sm border border-gray-300 bg-white hover:bg-gray-50" data-testid="admin-psttg-next" aria-label="Nächstes Jahr"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={notifyIncomplete} disabled={notifying} className="inline-flex items-center gap-1.5 rounded-sm bg-amber-500 text-white px-3 py-2 text-sm font-bold hover:bg-amber-600 disabled:opacity-50" data-testid="admin-psttg-notify">
                            {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Unvollständige anschreiben
                        </button>
                        <button onClick={() => dl("csv")} className="inline-flex items-center gap-1.5 rounded-sm bg-white border border-gray-300 px-3 py-2 text-sm font-bold hover:bg-gray-50" data-testid="admin-psttg-export-csv"><FileDown className="w-4 h-4" /> CSV</button>
                        <button onClick={() => dl("xml")} className="inline-flex items-center gap-1.5 rounded-sm bg-[#111827] text-white px-3 py-2 text-sm font-bold hover:bg-black" data-testid="admin-psttg-export-xml"><FileText className="w-4 h-4" /> XML</button>
                    </div>
                </div>

                {busy && <div className="flex items-center gap-2 text-gray-500 text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>}

                {s && !busy && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                            <Kpi label="Anbieter gesamt" value={s.sellers_total} testId="admin-psttg-kpi-total" />
                            <Kpi label="Meldepflichtig" value={s.reportable_sellers} accent="text-amber-700" testId="admin-psttg-kpi-reportable" />
                            <Kpi label="Stammdaten unvollständig" value={s.missing_data_sellers} accent={s.missing_data_sellers > 0 ? "text-red-600" : "text-emerald-600"} testId="admin-psttg-kpi-missing" />
                            <Kpi label="Erlös meldepflichtig" value={eur(s.reportable_gross_revenue)} testId="admin-psttg-kpi-revenue" />
                        </div>

                        {s.missing_data_sellers > 0 && (
                            <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="admin-psttg-warning">
                                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                <span>{s.missing_data_sellers} meldepflichtige Anbieter haben unvollständige Stammdaten (z. B. fehlende Steuer-IdNr.). Diese Daten müssen vor der BZSt-Meldung ergänzt werden.</span>
                            </div>
                        )}

                        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                            <table className="w-full text-sm" data-testid="admin-psttg-table">
                                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                                    <tr>
                                        <th className="px-3 py-2">Anbieter</th>
                                        <th className="px-3 py-2">Steuer-IdNr / USt-IdNr</th>
                                        <th className="px-3 py-2 text-right">Verkäufe</th>
                                        <th className="px-3 py-2 text-right">Erlös brutto</th>
                                        <th className="px-3 py-2 text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rep.sellers.map((row) => (
                                        <tr key={row.owner_id} className="border-t border-gray-100" data-testid={`admin-psttg-row-${row.owner_id}`}>
                                            <td className="px-3 py-2">
                                                <div className="font-bold text-gray-900">{row.company_name || row.contact_name || row.owner_id}</div>
                                                <div className="text-xs text-gray-500">{[row.address, row.postal_code, row.city].filter(Boolean).join(", ") || "— keine Anschrift —"}</div>
                                            </td>
                                            <td className="px-3 py-2">
                                                {row.vat_id || row.tax_id
                                                    ? <span className="font-mono-num">{row.vat_id || row.tax_id}</span>
                                                    : <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> fehlt</span>}
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono-num">{row.sales_count}</td>
                                            <td className="px-3 py-2 text-right font-mono-num">{eur(row.gross_revenue)}</td>
                                            <td className="px-3 py-2 text-center">
                                                {row.reportable
                                                    ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-bold">meldepflichtig</span>
                                                    : <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-xs font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> freigestellt</span>}
                                            </td>
                                        </tr>
                                    ))}
                                    {rep.sellers.length === 0 && (
                                        <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">Keine Verkäufe im Meldejahr {year}.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <p className="mt-4 text-xs text-gray-400 max-w-3xl" data-testid="admin-psttg-disclaimer">{rep.disclaimer}</p>
                    </>
                )}
            </div>
        </div>
    );
}

function Kpi({ label, value, accent = "text-gray-900", testId }) {
    return (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3" data-testid={testId}>
            <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
            <div className={`font-display text-2xl font-black ${accent}`}>{value}</div>
        </div>
    );
}
